// ============================================================
//  Bragsheet React — data/welcomeEmail.js
//  Fires the welcome-email Cloudflare Worker on signup. Ported from the vanilla
//  firebase.js Auth._triggerWelcomeEmail.
//
//  CORS status (verified 2026-09-18): the Worker at
//  welcome-email.wiseadadon.workers.dev now handles the OPTIONS preflight
//  (returns 204) and echoes Access-Control-Allow-Origin for the app's origins
//  — localhost:5173 (dev), testing.bragsheet.net (preview), app.bragsheet.net
//  (prod), and the native capacitor://localhost / https://localhost origins.
//  (The older "405 on preflight" note in www/firebase.js is stale; the 405 was
//  fixed and the allowlist was extended to the dev/preview/native origins.)
//
//  This is fire-and-forget: signup must NEVER be blocked or failed by it. It
//  uses a short client-side timeout and only logs on failure.
// ============================================================

const WELCOME_EMAIL_URL = 'https://welcome-email.wiseadadon.workers.dev';
const TIMEOUT_MS = 4000; // don't hold up signup for more than this

/**
 * Trigger the welcome email. Resolves to a result object but callers typically
 * ignore it and do not await it on the critical signup path.
 * @param {string} email
 * @param {string} name
 * @returns {Promise<{success:boolean, status?:number, error?:unknown}>}
 */
export async function triggerWelcomeEmail(email, name) {
  const payload = {
    email: String(email || '').trim(),
    name: String(name || '').trim(),
  };
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  if (controller) setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const res = await fetch(WELCOME_EMAIL_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: controller ? controller.signal : undefined,
    });
    if (!res.ok) {
      console.warn('[Welcome email] Worker returned non-OK status:', res.status);
      return { success: false, status: res.status };
    }
    return { success: true };
  } catch (err) {
    if (err && err.name === 'AbortError') {
      console.warn('[Welcome email] Request timed out after', TIMEOUT_MS, 'ms');
    } else {
      console.warn('[Welcome email] Failed to trigger (non-critical):', err);
    }
    return { success: false, error: err };
  }
}
